import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useTeamAccess, useUpdateRecordingRule, type RecordingRule } from '@/api/team'

const REGLES: RecordingRule[] = ['own', 'team', 'managers']

/**
 * The company rule for members, on one line: « Members hear: [their own calls] ».
 * The « ? » opens the detail; a member's exception lives in their panel.
 */
export function RecordingAccessSection() {
  const { t } = useTranslation()
  const { data: etat } = useTeamAccess()
  const update = useUpdateRecordingRule()
  const [aide, setAide] = useState(false)

  if (!etat) return null

  const choisir = (rule: RecordingRule) => {
    if (rule === etat.rule) return
    update.mutate(rule, {
      onSuccess: () => toast.success(t('recordingAccess.saved')),
      onError: () => toast.error(t('recordingAccess.saveError')),
    })
  }

  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <label htmlFor="regle-ecoute" className="text-sm font-medium text-slate-900 dark:text-white">
          {t('recordingAccess.ruleLabel')}
        </label>
        <Select value={etat.rule} onValueChange={(v) => choisir(v as RecordingRule)} disabled={!etat.can_edit || update.isPending}>
          <SelectTrigger id="regle-ecoute" className="h-9 w-auto min-w-[190px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {REGLES.map((r) => (
              <SelectItem key={r} value={r}>{t(`recordingAccess.rule.${r}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={() => setAide((v) => !v)}
          aria-expanded={aide}
          aria-controls="regle-ecoute-aide"
          aria-label={t('recordingAccess.ruleHelpLabel')}
          title={t('recordingAccess.ruleHelpLabel')}
          className={cn(
            'h-6 w-6 rounded-full border text-xs font-semibold transition-colors',
            aide
              ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-400 dark:bg-indigo-500/10 dark:text-indigo-300'
              : 'border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-700 dark:border-slate-600 dark:text-slate-400 dark:hover:text-slate-200',
          )}
        >
          ?
        </button>
        {update.isPending && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>
      {aide && (
        <p
          id="regle-ecoute-aide"
          className="mt-2 max-w-xl rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300"
        >
          {t('recordingAccess.ruleHelp')}
        </p>
      )}
    </section>
  )
}
